import { z } from 'zod';
/**
 * Ground terrains of the open world (ADR 0010, ADR 0011). Data, not code.
 *
 * A terrain is what a tile of a zone's `ground` rows is made of. Collision
 * follows `walkable`: the generator writes `#` in `rows` wherever the
 * terrain is not. `layer` decides who spreads at the corners: a vertex of
 * the grid takes the highest-layer terrain among the four tiles around it
 * (world/terrain-field.ts), so mountains and lakes are painted whole and
 * a trail one tile wide reads as two.
 *
 * Tiles are Wang tiles chosen by their four corners, and a Wang tileset
 * knows exactly two terrains. So a tile can only be drawn when its corners
 * hold one terrain, or two that share a tileset in `TERRAIN_TILESETS`. The
 * generator keeps every other pair apart; a test proves it.
 */
export const TerrainDefinitionSchema = z.object({
    id: z.string().min(1),
    /** One character, as written in a zone's `ground` rows. */
    char: z.string().length(1),
    /** French, for the minimap legend and tooltips. */
    name: z.string().min(1),
    walkable: z.boolean(),
    /** Higher spreads over lower at the corners. */
    layer: z.number().int().min(0),
    /** Minimap colour. */
    colour: z.string().regex(/^#[0-9a-f]{6}$/),
    /**
     * Painted as this other terrain. A bridge is walkable, but the tiles under
     * it are the river's: the planks are a prop drawn on top.
     */
    drawAs: z.string().length(1).optional(),
});
export const GRASS = '.';
/** A mountain's walkable top (ADR 0021). */
export const SUMMIT = 'k';
const ROCK_CHAR = 'r';
/** The height a summit stands at: above every terrace, so its cliff is its own. */
export const SUMMIT_LEVEL = 3;
/** The summit's cliff: the rock under it on the low side, the summit on the high. */
export const SUMMIT_CLIFF = 'tiles.cliff-summit';
export const CAVE_GROUNDS = [
    {
        biome: 'meadow',
        floor: 'c',
        wall: 'x',
        tileset: 'tiles.cave',
        colours: ['#58422f', '#2a1c1a'],
    },
    {
        biome: 'forest',
        floor: 'o',
        wall: 'O',
        tileset: 'tiles.cave-forest',
        colours: ['#4c5230', '#1c2a22'],
    },
    {
        biome: 'swamp',
        floor: 'u',
        wall: 'U',
        tileset: 'tiles.cave-swamp',
        colours: ['#3d4f2c', '#1a2e2c'],
    },
    {
        biome: 'desert',
        floor: 'y',
        wall: 'Y',
        tileset: 'tiles.cave-desert',
        colours: ['#8a6a3a', '#4a2c22'],
    },
    {
        biome: 'snow',
        floor: 'v',
        wall: 'V',
        tileset: 'tiles.cave-snow',
        colours: ['#8aa0b8', '#3a4c66'],
    },
];
const DEFINITIONS = [
    { id: 'grass', char: GRASS, name: 'Herbe', walkable: true, layer: 0, colour: '#4f8a3a' },
    { id: 'sand', char: 's', name: 'Sable', walkable: true, layer: 1, colour: '#d9b86a' },
    { id: 'snow', char: 'n', name: 'Neige', walkable: true, layer: 1, colour: '#e6eef5' },
    { id: 'marsh', char: 'h', name: 'Marais', walkable: true, layer: 1, colour: '#5b6b3a' },
    { id: 'dirt', char: 'd', name: 'Terre', walkable: true, layer: 2, colour: '#9a7648' },
    { id: 'thicket', char: 'f', name: 'Fourré', walkable: false, layer: 3, colour: '#23421f' },
    { id: 'water', char: 'w', name: 'Eau', walkable: false, layer: 3, colour: '#3a6fb0' },
    { id: 'bog', char: 'm', name: 'Marécage', walkable: false, layer: 3, colour: '#3f5a2e' },
    { id: 'ice', char: 'i', name: 'Glace', walkable: false, layer: 3, colour: '#9cc8e6' },
    { id: 'rock', char: 'r', name: 'Roche', walkable: false, layer: 4, colour: '#7a7a84' },
    {
        id: 'bridge',
        char: 'b',
        name: 'Pont',
        walkable: true,
        layer: 3,
        colour: '#8a5a2b',
        drawAs: 'w',
    },
    // Summits (ADR 0021): the walkable top of a mountain, above its rock,
    // reached only through a cave. Highest layer: it is always painted whole,
    // its cliff drawn by the relief on the rock around it.
    { id: 'summit', char: 'k', name: 'Sommet', walkable: true, layer: 5, colour: '#a9a9b8' },
    // Inside a cave (ADR 0021, ADR 0062): each biome's floor and the walls
    // around it, from CAVE_GROUNDS.
    ...CAVE_GROUNDS.flatMap((ground) => [
        {
            id: `cave-floor-${ground.biome}`,
            char: ground.floor,
            name: 'Grotte',
            walkable: true,
            layer: 0,
            colour: ground.colours[0],
        },
        {
            id: `cave-wall-${ground.biome}`,
            char: ground.wall,
            name: 'Paroi',
            walkable: false,
            layer: 1,
            colour: ground.colours[1],
        },
    ]),
    // Inside a boss's dungeon: flagstones and the brick walls around them.
    { id: 'dungeon-floor', char: 'q', name: 'Dalles', walkable: true, layer: 0, colour: '#5d5d66' },
    { id: 'dungeon-wall', char: 'Q', name: 'Muraille', walkable: false, layer: 1, colour: '#26242c' },
    // Cliffs (ADR 0020): the face between two heights, closed to walking. Each
    // is its ground as far as the ground's tiles go (same layer, painted as
    // it); the relief draws the wall over it.
    {
        id: 'cliff-grass',
        char: 'G',
        name: 'Falaise',
        walkable: false,
        layer: 0,
        colour: '#58422f',
        drawAs: GRASS,
    },
    {
        id: 'cliff-sand',
        char: 'S',
        name: 'Falaise',
        walkable: false,
        layer: 1,
        colour: '#81442e',
        drawAs: 's',
    },
    {
        id: 'cliff-snow',
        char: 'N',
        name: 'Falaise',
        walkable: false,
        layer: 1,
        colour: '#6b6b7c',
        drawAs: 'n',
    },
    {
        id: 'cliff-marsh',
        char: 'H',
        name: 'Falaise',
        walkable: false,
        layer: 1,
        colour: '#2a1c1a',
        drawAs: 'h',
    },
];
export const TERRAINS = DEFINITIONS.map((raw) => TerrainDefinitionSchema.parse(raw));
const GRASS_TERRAIN = TerrainDefinitionSchema.parse(DEFINITIONS[0]);
const BY_CHAR = new Map(TERRAINS.map((terrain) => [terrain.char, terrain]));
/** The terrain a ground character stands for; unknown characters read as grass. */
export function terrainOf(char) {
    return BY_CHAR.get(char) ?? GRASS_TERRAIN;
}
export function isTerrainChar(char) {
    return BY_CHAR.has(char);
}
export const TERRAIN_TILESETS = [
    { key: 'tiles.grass-dirt', upper: GRASS, lower: 'd' },
    { key: 'tiles.forest', upper: GRASS, lower: 'f' },
    { key: 'tiles.water', upper: GRASS, lower: 'w' },
    { key: 'tiles.rock', upper: GRASS, lower: 'r' },
    { key: 'tiles.sand', upper: GRASS, lower: 's' },
    { key: 'tiles.snow', upper: GRASS, lower: 'n' },
    { key: 'tiles.marsh', upper: GRASS, lower: 'h' },
    { key: 'tiles.sand-rock', upper: 's', lower: 'r' },
    { key: 'tiles.snow-rock', upper: 'n', lower: 'r' },
    { key: 'tiles.snow-ice', upper: 'n', lower: 'i' },
    // The same lake water against each biome's own ground: no meadow banks in
    // the desert, the snow or the swamp (the water base tile is shared).
    { key: 'tiles.sand-water', upper: 's', lower: 'w' },
    { key: 'tiles.snow-water', upper: 'n', lower: 'w' },
    { key: 'tiles.marsh-water', upper: 'h', lower: 'w' },
    // The swamp's water meets only the swamp's ground.
    { key: 'tiles.marsh-bog', upper: 'h', lower: 'm' },
    // Where the desert and the snow meet the swamp, without a strip of meadow between.
    { key: 'tiles.sand-marsh', upper: 's', lower: 'h' },
    { key: 'tiles.snow-marsh', upper: 'n', lower: 'h' },
    // The swamp against the mountains of the rim.
    { key: 'tiles.marsh-rock', upper: 'h', lower: 'r' },
    // Roads run through every biome: packed earth meets every ground.
    { key: 'tiles.sand-dirt', upper: 's', lower: 'd' },
    { key: 'tiles.snow-dirt', upper: 'n', lower: 'd' },
    { key: 'tiles.marsh-dirt', upper: 'h', lower: 'd' },
    // Inside the caves (ADR 0021): the floor meets only its walls.
    ...CAVE_GROUNDS.map((ground) => ({
        key: ground.tileset,
        upper: ground.wall,
        lower: ground.floor,
    })),
    // Inside the bosses' dungeons: the flagstones meet only their walls.
    { key: 'tiles.dungeon', upper: 'Q', lower: 'q' },
];
export const CLIFF_TILESETS = [
    { ground: GRASS, cliff: 'G', key: 'tiles.cliff-grass', stairs: 'stairs-grass' },
    { ground: 's', cliff: 'S', key: 'tiles.cliff-sand', stairs: 'stairs-sand' },
    { ground: 'n', cliff: 'N', key: 'tiles.cliff-snow', stairs: 'stairs-snow' },
    { ground: 'h', cliff: 'H', key: 'tiles.cliff-marsh', stairs: 'stairs-marsh' },
];
/** The cliff of a ground, if that ground can rise. */
export function cliffOf(ground) {
    return CLIFF_TILESETS.find((cliff) => cliff.ground === ground);
}
/** Whether a manifest key is a cliff tileset: 25 tiles in a 5×5 grid, not 16 by corner mask. */
export function isCliffTileset(key) {
    return key === SUMMIT_CLIFF || CLIFF_TILESETS.some((cliff) => cliff.key === key);
}
export function isCliff(char) {
    return CLIFF_TILESETS.some((cliff) => cliff.cliff === char);
}
/** Every tileset a zone may draw from: the terrain pairs, then the cliffs. */
export const ALL_TILESET_KEYS = [
    ...TERRAIN_TILESETS.map((tileset) => tileset.key),
    ...CLIFF_TILESETS.map((cliff) => cliff.key),
    SUMMIT_CLIFF,
];
/** The tileset that draws the meeting of two terrains, if any. */
export function tilesetFor(a, b) {
    return TERRAIN_TILESETS.find((tileset) => (tileset.upper === a && tileset.lower === b) || (tileset.upper === b && tileset.lower === a));
}
/** The terrain a tile is painted as: itself, or what lies under it (a bridge's river). */
export function paintedAs(char) {
    const terrain = terrainOf(char);
    return terrain.drawAs ?? terrain.char;
}
/** Whether two terrains may meet at the corners of one tile. */
export function terrainsMeet(a, b) {
    if (a === b || tilesetFor(a, b) !== undefined)
        return true;
    // A summit meets only the rock under it, and the summit's cliff draws them.
    return (a === SUMMIT && b === ROCK_CHAR) || (a === ROCK_CHAR && b === SUMMIT);
}
/** A tileset that holds this terrain, for tiles made of it alone. */
export function tilesetOf(char) {
    return TERRAIN_TILESETS.find((tileset) => tileset.upper === char || tileset.lower === char);
}
//# sourceMappingURL=terrains.js.map